/**
 * Per-connection `azure-storage:account-info` snapshot (spec §4.5): which
 * services are reachable, HNS, kind / sku. Read synchronously by the tree
 * factory (`ConnectionNodeBehaviors.initializeDefaultStructure`) and by the
 * connection-info dialog (unavailable-service reasons); filled by
 * `useStorageAccountInfoSync`, forgotten on disconnect. Every probe carries
 * the token `setLoading` handed out: a reply whose token no longer matches the
 * entry (the connection was forgotten or re-probed meanwhile) is dropped, so a
 * late answer of a dead connect never resurrects a stale snapshot (review r2).
 * Every entry also records the connection GENERATION it was probed for (the
 * active connection's `connectedAt`): an in-place Reconnect never leaves
 * `connected`, so the generation is the only thing that tells the hook this
 * snapshot belongs to a superseded attempt (review r3).
 */
import { create } from 'zustand';
import type { StorageAccountInfo, StorageOperationError } from '../../../types/azureStorage';

type ProbedFor = {
  /** The connect this entry belongs to (`ActiveConnection.connectedAt`, 0 when the entry predates one). */
  generation: number;
};

export type StorageAccountInfoEntry =
  | ({ status: 'loading'; token: number } & ProbedFor)
  | ({ status: 'ready'; info: StorageAccountInfo } & ProbedFor)
  | ({ status: 'error'; error: StorageOperationError } & ProbedFor);

type StorageAccountInfoState = {
  byConnection: Record<string, StorageAccountInfoEntry>;
  /** Starts a probe for `generation`; the returned token fences its reply. */
  setLoading: (connectionId: string, generation?: number) => number;
  /** Applied only while the entry is still the probe `token` started; returns whether it was. */
  setInfo: (connectionId: string, info: StorageAccountInfo, token: number) => boolean;
  setError: (connectionId: string, error: StorageOperationError, token: number) => boolean;
  forget: (connectionId: string) => void;
};

let probeCounter = 0;

export const useStorageAccountInfoStore = create<StorageAccountInfoState>((set, get) => {
  const settle = (connectionId: string, token: number, entry: { status: 'ready'; info: StorageAccountInfo } | { status: 'error'; error: StorageOperationError }): boolean => {
    const current = get().byConnection[connectionId];
    if (current?.status !== 'loading' || current.token !== token) return false;
    set((s) => ({ byConnection: { ...s.byConnection, [connectionId]: { ...entry, generation: current.generation } as StorageAccountInfoEntry } }));
    return true;
  };
  return {
  byConnection: {},
  setLoading: (connectionId, generation = 0) => {
    const token = ++probeCounter;
    set((s) => ({ byConnection: { ...s.byConnection, [connectionId]: { status: 'loading', token, generation } } }));
    return token;
  },
  setInfo: (connectionId, info, token) => settle(connectionId, token, { status: 'ready', info }),
  setError: (connectionId, error, token) => settle(connectionId, token, { status: 'error', error }),
  forget: (connectionId) =>
    set((s) => {
      if (!s.byConnection[connectionId]) return s;
      const next = { ...s.byConnection };
      delete next[connectionId];
      return { byConnection: next };
    }),
  };
});

export function getStorageAccountInfoSnapshot(connectionId: string): StorageAccountInfo | undefined {
  const entry = useStorageAccountInfoStore.getState().byConnection[connectionId];
  return entry?.status === 'ready' ? entry.info : undefined;
}
